import React from "react";
import { format, startOfMonth, endOfMonth, addDays, getDay } from "date-fns";

import { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";

interface MonthViewProps {
  currentMonthStart: Date;
  today: Date;
  appointments: any[];
  selectedDate: string;
  setSelectedDate: Dispatch<SetStateAction<string>>;
  forceSelectToday?: boolean;
  viewMode?: string;
}

const MonthView: React.FC<MonthViewProps> = ({
  currentMonthStart,
  today,
  appointments,
  selectedDate,
  setSelectedDate,
  forceSelectToday,
}) => {
  React.useEffect(() => {
    const todayStr = format(today, "yyyy-MM-dd");
    if (forceSelectToday) {
      setSelectedDate(todayStr);
    } else if (!selectedDate) {
      setSelectedDate(todayStr);
    }
  }, [forceSelectToday, today, selectedDate, setSelectedDate]);

  const { t } = useTranslation();

  const monthStart = startOfMonth(currentMonthStart);
  const monthEnd = endOfMonth(currentMonthStart);

  let startDay = getDay(monthStart);
  startDay = startDay === 0 ? 6 : startDay - 1;

  const days: Date[] = [];
  for (let d = monthStart; d <= monthEnd; d = addDays(d, 1)) {
    days.push(d);
  }

  const leadingEmpty = Array(startDay).fill(null);

  const weeks: (Date | null)[][] = [];
  let week: (Date | null)[] = [];
  [...leadingEmpty, ...days].forEach((day) => {
    week.push(day);
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  });
  if (week.length) weeks.push(week);

  // Filter appointments for approved status
  const approvedAppointments = appointments.filter(
    (a) => a.status === "approved"
  );

  // Map: yyyy-MM-dd => appointments[]
  const appointmentsByDate: { [date: string]: any[] } = {};
  approvedAppointments.forEach((a) => {
    let dateStr = "";
    if (a.startTime) {
      const d = new Date(a.startTime);
      if (!isNaN(d.getTime())) {
        dateStr = format(d, "yyyy-MM-dd");
      }
    }
    if (dateStr) {
      if (!appointmentsByDate[dateStr]) appointmentsByDate[dateStr] = [];
      appointmentsByDate[dateStr].push(a);
    }
  });

  // Appointments for selected date
  const selectedAppointments = appointmentsByDate[selectedDate] || [];

  // Responsive: show events inside each cell for desktop/tablet
  // Responsive: use matchMedia for SSR safety
  const [isTabletOrDesktop, setIsTabletOrDesktop] = React.useState(false);
  React.useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    setIsTabletOrDesktop(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsTabletOrDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  if (!isTabletOrDesktop) {
    // MOBILE: original layout and event list
    return (
      <>
        <div className="w-full flex justify-center mt-4">
          <div className="p-3 rounded-2xl border border-orange-400 inline-block">
            <div className="grid grid-cols-7 gap-x-4 md:gap-x-20 lg:gap-x-20 gap-y-4 mb-1">
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                <div
                  key={day}
                  className="text-center font-bold py-2 text-gray-700"
                >
                  {day}
                </div>
              ))}
            </div>
            {weeks.map((week, i) => (
              <div
                className="grid grid-cols-7 gap-x-4 md:gap-x-20 lg:gap-x-20 gap-y-4"
                key={i}
              >
                {week.map((day, j) => {
                  if (!day) {
                    return <div key={j} className="w-10 h-10" />;
                  }
                  const dayStr = format(day, "yyyy-MM-dd");
                  const isToday = dayStr === format(today, "yyyy-MM-dd");
                  const isSelected = dayStr === selectedDate;
                  const hasApproved = appointmentsByDate[dayStr]?.length > 0;
                  return (
                    <div
                      key={j}
                      className="h-12 flex flex-col items-center justify-center"
                    >
                      <button
                        type="button"
                        className={
                          `w-10 h-10 flex items-center justify-center rounded-full transition-all ` +
                          (isToday
                            ? "bg-orange-500 text-white shadow"
                            : isSelected
                              ? "border border-orange-500 bg-white"
                              : "bg-white text-gray-700")
                        }
                        onClick={() => setSelectedDate(dayStr)}
                      >
                        {format(day, "d")}
                      </button>
                      {/* Dot for approved appointments */}
                      {hasApproved && (
                        <span
                          className={
                            `w-1.5 h-1.5 rounded-full block mx-auto relative top-[-8px] ` +
                            (isToday
                              ? "bg-white"
                              : isSelected
                                ? "bg-orange-700"
                                : "bg-orange-700")
                          }
                        ></span>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
        {/* List appointments for selected date */}
        <div className="w-full flex justify-center mt-4">
          <div className="w-full max-w-3xl lg:h-[33vh] h-[27vh] md:h[30vh] overflow-y-auto">
            {selectedAppointments.length > 0 ? (
              <ul>
                {selectedAppointments.map((a, idx) => {
                  // Format time range
                  let timeStr = "";
                  if (a.startTime && a.endTime) {
                    const start = new Date(a.startTime);
                    const end = new Date(a.endTime);
                    if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
                      const pad = (n: number) => n.toString().padStart(2, "0");
                      timeStr = `${pad(start.getHours())}:${pad(start.getMinutes())} - ${pad(end.getHours())}:${pad(end.getMinutes())}`;
                    }
                  }
                  return (
                    <li key={idx} className="flex items-start mb-4">
                      <div
                        className="w-1 h-full bg-blue-500 rounded mr-3"
                        style={{ minHeight: "48px" }}
                      ></div>
                      <div>
                        <div className="font-bold text-md mb-1">
                          {timeStr || "Time not set"}
                        </div>
                        <div className="text-base">
                          {a.title || a.name || "Appointment"}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="text-center text-gray-400 text-base">
                {t("appointment.noEvent")}
              </div>
            )}
          </div>
        </div>
      </>
    );
  }
  // DESKTOP/TABLET: new layout and event display
  const daysOfWeekEn = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  return (
    <>
      <div className="w-full flex justify-center mt-4">
        <div className="p-3 rounded-2xl inline-block">
          <div className="grid grid-cols-7 gap-x-2 gap-y-2 mb-1">
            {daysOfWeekEn.map((day) => (
              <div
                key={day}
                className="text-center font-bold py-2 text-gray-700"
              >
                {day}
              </div>
            ))}
          </div>
          {weeks.map((week, i) => (
            <div className="grid grid-cols-7 gap-x-2 gap-y-2" key={i}>
              {week.map((day, j) => {
                if (!day) {
                  return (
                    <div
                      key={j}
                      className="h-22 md:h-28 lg:h-[124px] bg-gray-50 rounded-lg"
                    />
                  );
                }
                const dayStr = format(day, "yyyy-MM-dd");
                const isToday = dayStr === format(today, "yyyy-MM-dd");
                const isSelected = dayStr === selectedDate;
                return (
                  <div
                    key={j}
                    className={`w-full md:min-w-[124px] lg:min-w-[164px] flex-1 bg-white rounded-lg border flex flex-col p-1 h-22 md:h-28 lg:h-[124px] ${isToday ? "border-orange-500" : "border-gray-200"} ${isSelected ? "shadow-lg" : ""}`}
                    onClick={() => setSelectedDate(dayStr)}
                    style={{
                      cursor: "pointer",
                      width: window.innerWidth >= 1920 ? "15rem" : undefined,
                    }}
                  >
                    <div
                      className={`font-bold text-xs md:text-sm lg:text-base mb-1 ${isToday ? "text-orange-500" : "text-gray-700"}`}
                    >
                      {format(day, "d")}
                    </div>
                    <div className="flex-1 flex flex-col justify-start min-h-[20px]">
                      {appointmentsByDate[dayStr]?.length > 0 ? (
                        appointmentsByDate[dayStr].map((a, idx) => {
                          let timeStr = "";
                          if (a.startTime && a.endTime) {
                            const start = new Date(a.startTime);
                            const end = new Date(a.endTime);
                            if (
                              !isNaN(start.getTime()) &&
                              !isNaN(end.getTime())
                            ) {
                              const pad = (n: number) =>
                                n.toString().padStart(2, "0");
                              timeStr = `${pad(start.getHours())}:${pad(start.getMinutes())}`;
                            }
                          }
                          return (
                            <div
                              key={idx}
                              className="text-[10px] md:text-xs lg:text-sm text-gray-600 truncate mb-1 flex items-center gap-1"
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-orange-400 inline-block"></span>
                              <span className="font-semibold">{timeStr}</span>{" "}
                              {a.title || a.name || "Appointment"}
                            </div>
                          );
                        })
                      ) : (
                        <div className="min-h-[20px]" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default MonthView;
